import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { Send, ShieldCheck } from "lucide-react";

const NewsletterForm = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = (e) => {
    e.preventDefault();
    const err = {};
    if (!name.trim()) err.name = t("form.error_name");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) err.email = t("form.error_email");
    if (!consent) err.consent = t("form.error_consent");
    setErrors(err);
    if (Object.keys(err).length === 0) {
      try {
        const existing = JSON.parse(localStorage.getItem("ecuasuelo_leads") || "[]");
        existing.push({ name, email, ts: Date.now() });
        localStorage.setItem("ecuasuelo_leads", JSON.stringify(existing));
      } catch (e) {}
      navigate("/thank_you");
    }
  };

  return (
    <section
      id="lead-form"
      data-testid="lead-form-section"
      className="relative py-24 bg-slate-950 text-white overflow-hidden"
    >
      <div className="absolute inset-0 grid-pattern-dark opacity-40 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="eyebrow eyebrow-dark mb-4">{t("form.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              {t("form.title")}
            </h2>
            <p className="text-lg text-slate-300 mb-6">{t("form.subtitle")}</p>
            <p className="text-slate-400 leading-relaxed mb-8">
              {t("form.description")}
            </p>
            <div className="flex items-start gap-3 p-4 rounded-md border border-slate-800 bg-slate-900/60">
              <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300">{t("form.note")}</p>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="lead-form"
            className="bg-white text-slate-900 rounded-xl p-6 sm:p-8 shadow-2xl border border-slate-200"
            noValidate
          >
            <div className="mb-5">
              <label
                htmlFor="lead-name"
                className="block text-sm font-semibold mb-2 text-slate-800"
              >
                {t("form.name_label")}
              </label>
              <input
                id="lead-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("form.name_placeholder")}
                data-testid="lead-form-name-input"
                className="w-full px-4 py-3 rounded-md border border-slate-300 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-slate-900"
              />
              {errors.name && (
                <p data-testid="lead-form-name-error" className="text-xs text-red-600 mt-1">
                  {errors.name}
                </p>
              )}
            </div>

            <div className="mb-5">
              <label
                htmlFor="lead-email"
                className="block text-sm font-semibold mb-2 text-slate-800"
              >
                {t("form.email_label")}
              </label>
              <input
                id="lead-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("form.email_placeholder")}
                data-testid="lead-form-email-input"
                className="w-full px-4 py-3 rounded-md border border-slate-300 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-slate-900"
              />
              {errors.email && (
                <p data-testid="lead-form-email-error" className="text-xs text-red-600 mt-1">
                  {errors.email}
                </p>
              )}
            </div>

            <label className="flex items-start gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="lead-form-consent-checkbox"
                className="mt-1 w-4 h-4 rounded border-slate-300 accent-emerald-500"
              />
              <span className="text-xs text-slate-600 leading-relaxed">
                {t("form.consent")}
              </span>
            </label>
            {errors.consent && (
              <p data-testid="lead-form-consent-error" className="text-xs text-red-600 -mt-4 mb-4">
                {errors.consent}
              </p>
            )}

            <button
              type="submit"
              data-testid="lead-form-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-md bg-slate-900 text-white font-semibold hover:bg-slate-800"
            >
              <Send className="w-4 h-4" />
              {t("form.submit")}
            </button>
            <p className="text-xs text-slate-500 mt-4 text-center">{t("form.footnote")}</p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default NewsletterForm;
